import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { z } from 'zod';

import type { ToolExecutionContext } from '../types';

import { resolveSceneHandle } from './scene-addressing';
import { applySceneWatermarkEdit } from './scene-watermark-edit';
import { VideoStateTool } from './VideoStateTool';

export const setWatermarkSchema = z.object({
    scene_id: z.string().describe('The scene_id from the video state.'),
    visibility: z
        .boolean()
        .optional()
        .describe(
            'The visibility of the watermark. Provide this to change watermark visibility (show/hide).',
        ),
    watermark_source_index: z
        .int()
        .optional()
        .describe(
            'The index of the watermark to apply as watermark. Must be an index from the template watermark sources.',
        ),
    position: z
        .enum(['top_left', 'top_right', 'bottom_left', 'bottom_right'])
        .optional()
        .describe(
            'The position where to place the watermark. Provide this to change watermark position.',
        ),
});
export type SetWatermarkArgs = z.infer<typeof setWatermarkSchema>;

export class SetWatermarkTool extends VideoStateTool<typeof setWatermarkSchema> {
    protected override readonly schema = setWatermarkSchema;
    protected override readonly toolName = TOOL_NAMES.setWatermark;
    protected override readonly toolDescription =
        'Set watermark for a specific scene: watermark_source_index changes its source, position moves it, and visibility shows or hides it.';

    public override getToolDisplayName(): string {
        return 'Setting watermark';
    }

    public get statusIcon(): string {
        return 'branding_watermark';
    }

    public async execute(args: SetWatermarkArgs, context: ToolExecutionContext) {
        const { watermark_source_index, position, visibility } = args;

        // check if the user has the watermark capability
        const canSetWatermark = SERVER?.USER?.capabilities?.custom_watermark ?? false;
        if (!canSetWatermark) {
            return `Error: Current plan does not support custom watermarks. Use ${TOOL_NAMES.suggestUpgrade} tool to show the user that they need to upgrade.`;
        }

        try {
            const { sceneIndex: scene_index, sceneShortId } = resolveSceneHandle(
                this.luminary,
                this.getState(),
                args,
            );
            const scene = this.luminary.scenes[scene_index];

            return applySceneWatermarkEdit(
                this.luminary,
                this.dispatch,
                scene,
                sceneShortId,
                { visibility, watermark_source_index, position },
                context.parentActionGroup,
            );
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error setting watermark: ${errorMessage}`;
        }
    }
}
